import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../Store/Slices/AuthSlice";
import bingopayLogo from "../Assets/Images/logo_png_with_shreebalajee (1).png";
import useApi from "../CustomHooks/useApi";
import { initSession } from "../CustomHooks/cryptoService";
import { reauthWithMpin } from "../CustomHooks/reauthService";
import { describeMpinError } from "../CustomHooks/gatewayErrors";
import { verifyMpin } from "../CustomHooks/mpinFlow";
import { logoutFromGateway } from "../CustomHooks/gatewayLogout";

// Shown when the gateway's crypto session (AES key, 15 min idle TTL) has
// expired — the login token itself is still valid for up to 10h, this is
// purely an idle-lock re-prompt, not a full re-login. See
// gatewayAxios.js's response interceptor, which redirects here on
// "Session expired or invalid" instead of silently re-handshaking.
//
// Two modes (sessionStorage.mpinMode, set by gatewayAxios.js):
//  - "unlock" (default): the crypto session lapsed. Validates against
//    merchant-portal-backend's own (pre-existing, unmodified)
//    POST /api/bpay/staff/onboard/validate-mpin.
//  - "reauth": the gateway saw the login token used from a different IP (code
//    GWB001). POST /auth/reauth on the gateway re-binds the SAME token to the
//    new IP once the owner proves the MPIN — validate-mpin cannot do that.
function MpinVerify() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const employeeDetails = useSelector((state) => state.auth.employeeDetails);
  const displayName = employeeDetails?.others?.merchant_shop_name || employeeDetails?.username;

  const [mode] = useState(() => (sessionStorage.getItem("mpinMode") === "reauth" ? "reauth" : "unlock"));
  const [mpin, setMpin] = useState("");
  const [revealIndex, setRevealIndex] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const inputRef = useRef(null);
  const revealTimerRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(revealTimerRef.current);
  }, []);

  // Ticks the gateway-reported lockout down to 0 client-side (see
  // MpinLockoutWebFilter's lockedForSeconds) — only restarts the interval when
  // lockout starts/ends, not on every tick.
  useEffect(() => {
    if (lockoutSeconds <= 0) return undefined;
    const id = setInterval(() => {
      setLockoutSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockoutSeconds > 0]);

  // useAuth=true (default) — merchant-portal-backend's own SecurityConfig does NOT
  // have this path in its permitAll list (confirmed: 403 without a token), so it needs
  // the still-valid login JWT attached. validateMpin() itself doesn't read the token —
  // it's only there to satisfy Spring Security's .anyRequest().authenticated() gate.
  const { callApi } = useApi("/staff/onboard/validate-mpin", "POST");

  useEffect(() => {
    // Nothing to unlock — send to a real login instead of an MPIN prompt.
    if (!employeeDetails?.token) {
      navigate("/", { replace: true });
    }
  }, [employeeDetails, navigate]);

  const handleMpinChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setMpin(digits);

    clearTimeout(revealTimerRef.current);
    if (digits.length > mpin.length) {
      // Briefly show the just-typed digit, then mask it like a native PIN pad.
      setRevealIndex(digits.length - 1);
      revealTimerRef.current = setTimeout(() => setRevealIndex(null), 1000);
    } else {
      setRevealIndex(null);
    }
  };

  const roleDashboard = () => {
    if (employeeDetails?.role === "MASTER_DISTRIBUTOR") return "/masterdistributordashboard";
    if (employeeDetails?.role === "DISTRIBUTOR") return "/distributordashboard";
    return "/retailerdashboard";
  };

  // The MPIN screen only exists because the crypto session (short idle TTL) already expired once, or
  // because the gateway saw a new IP. A fresh crypto session gets created right before landing here, but
  // that's a brand-new TTL window too — if it also lapses before the user finishes typing, the unlock call
  // hits "Session expired" again. gatewayAxios deliberately skips its normal auto-redirect for this path
  // (would infinite-loop back to /mpin-verify), so verifyMpin() re-inits once and retries silently, and
  // also hops from validate-mpin to /auth/reauth if the gateway says the IP changed (GWB001).
  const submitMpin = () =>
    verifyMpin({
      mode,
      unlock: () => callApi({ mid: employeeDetails?.username, mpin }),
      reauth: () => reauthWithMpin({ mid: employeeDetails?.username, mpin, token: employeeDetails?.token }),
      initSession,
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockoutSeconds > 0) return;
    setErrorMessage("");

    if (mpin.length !== 6) {
      setErrorMessage("Enter your 6-digit MPIN.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await submitMpin();

      if (result.ok) {
        const redirectTo = sessionStorage.getItem("postMpinRedirect") || roleDashboard();
        sessionStorage.removeItem("postMpinRedirect");
        sessionStorage.removeItem("mpinMode");
        navigate(redirectTo, { replace: true });
      } else if (result.lockedForSeconds > 0) {
        setLockoutSeconds(result.lockedForSeconds);
      } else {
        setErrorMessage(result.message || "Invalid MPIN.");
      }
    } catch (err) {
      const outcome = describeMpinError(err);
      if (outcome.action === "logout") {
        // Token revoked / no longer valid (GWB002, GWA003, GWA004): only a fresh login helps.
        handleLogout();
        return;
      }
      if (outcome.action === "locked") {
        setLockoutSeconds(outcome.lockoutSeconds);
      } else if (outcome.action === "resession") {
        setErrorMessage("That took a bit long — please try again.");
      } else {
        setErrorMessage(outcome.message);
      }
    } finally {
      setIsLoading(false);
      setMpin("");
      setRevealIndex(null);
      clearTimeout(revealTimerRef.current);
    }
  };

  const handleLogout = async () => {
    await logoutFromGateway();      // frees this login's slot at the gateway (best effort, short wait); needs the token, so before clear()
    dispatch(logout());
    localStorage.clear();
    sessionStorage.removeItem("postMpinRedirect");
    sessionStorage.removeItem("mpinMode");
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4 font-sans">
      <div className="w-full max-w-md bg-white p-10 rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-50">
        <div className="text-center mb-8">
          <img src={bingopayLogo} alt="BingoPay" className="h-16 w-auto mx-auto mb-6" />
          <h2 className="text-[28px] font-bold text-[#1E293B]">{displayName || "Session Locked"}</h2>
          <p className="text-gray-400 mt-1">
            {mode === "reauth"
              ? "We noticed a change in your network. Enter your MPIN to continue."
              : "Enter your MPIN to unlock your session."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div
            className="flex justify-center gap-3"
            onClick={() => inputRef.current?.focus()}
          >
            {Array.from({ length: 6 }).map((_, i) => {
              const filled = i < mpin.length;
              const showDigit = filled && i === revealIndex;
              return (
                <div
                  key={i}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold border-2 transition-colors
                    ${showDigit
                      ? "border-[#3B82F6] bg-[#EBF2FA] text-[#1E293B]"
                      : filled
                        ? "border-[#3B82F6] bg-[#3B82F6]"
                        : "border-gray-300 bg-white"}`}
                >
                  {showDigit ? mpin[i] : ""}
                </div>
              );
            })}
          </div>

          <input
            ref={inputRef}
            type="text"
            value={mpin}
            onChange={handleMpinChange}
            maxLength={6}
            autoFocus
            inputMode="numeric"
            autoComplete="off"
            disabled={lockoutSeconds > 0}
            className="sr-only"
          />

          {(errorMessage || lockoutSeconds > 0) && (
            <div className="p-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold text-center">
              {lockoutSeconds > 0
                ? `Too many wrong MPIN attempts. Try again in ${lockoutSeconds}s.`
                : errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || lockoutSeconds > 0}
            className="w-full py-4 rounded-2xl font-bold text-white bg-gradient-to-r from-[#3B82F6] to-[#1E40AF] shadow-lg shadow-blue-500/30 disabled:opacity-60"
          >
            {lockoutSeconds > 0 ? `Locked (${lockoutSeconds}s)` : isLoading ? "Verifying..." : "Unlock"}
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="w-full text-sm font-bold text-gray-400"
          >
            Not you? Log out
          </button>
        </form>
      </div>
    </div>
  );
}

export default MpinVerify;
